import * as React from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  QUESTION_DIFFICULTY_LABEL,
  QUESTION_DIFFICULTY_ORDER,
  QUESTION_LANGUAGE_LABEL,
  QUESTION_TYPE_LABEL,
  QUESTION_TYPE_ORDER,
  SLUG_RULES,
  boundedNameSchema,
  deriveSlug,
  fieldError,
  slugProblemFor,
  type AssessmentTemplateCreate,
  type AssessmentTemplatePublic,
  type QuestionDifficulty,
  type QuestionLanguage,
  type QuestionType,
} from "@eduviaos/shared";
import { useAssessmentTopics, useCreateAssessmentTemplate } from "@/hooks/useAssessments";

/**
 * Phase H5 FE-1 — Create-template sheet.
 *
 * Captures the minimum viable template: a name (the slug is derived
 * from it and never shown), grade band, language, and a single initial
 * blueprint item so the
 * BE's `min_length=1` items constraint is satisfied. The rest of the
 * runtime config (timer, proctor profile, shuffle toggles, generation
 * mode, etc.) is edited on the detail page once the template exists —
 * which keeps this sheet short and the time-to-author-blueprint fast.
 */

const TEMPLATE_NAME_MAX = 200;
const templateNameSchema = boundedNameSchema(TEMPLATE_NAME_MAX, "Template name");
const DUPLICATE_NAME_MESSAGE = "Another template already has this name. Choose a different name.";

interface DraftState {
  name: string;
  description: string;
  gradeMin: number;
  gradeMax: number;
  language: QuestionLanguage;
  initialTopicSlug: string;
  initialCount: number;
  initialType: QuestionType;
  initialDifficulty: QuestionDifficulty | "__any__";
}

const EMPTY_DRAFT: DraftState = {
  name: "",
  description: "",
  gradeMin: 6,
  gradeMax: 8,
  language: "en",
  initialTopicSlug: "",
  initialCount: 10,
  initialType: "mcq_single",
  initialDifficulty: "medium",
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (template: AssessmentTemplatePublic) => void;
}

export function AssessmentTemplateCreateSheet({ open, onOpenChange, onCreated }: Props) {
  const [draft, setDraft] = React.useState<DraftState>(EMPTY_DRAFT);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [showNameError, setShowNameError] = React.useState(false);
  const [serverNameError, setServerNameError] = React.useState<string | null>(null);

  const topicsQuery = useAssessmentTopics({ enabled: open });
  const createMutation = useCreateAssessmentTemplate();

  const topics = React.useMemo(() => topicsQuery.data?.items ?? [], [topicsQuery.data]);

  React.useEffect(() => {
    if (!open) return;
    setDraft({
      ...EMPTY_DRAFT,
      initialTopicSlug: topics[0]?.slug ?? "",
    });
    setErrorMessage(null);
    setShowNameError(false);
    setServerNameError(null);
  }, [open, topics]);

  const handleNameChange = (next: string) => {
    setServerNameError(null);
    setDraft((p) => ({ ...p, name: next }));
  };

  const derivedSlug = deriveSlug(draft.name, SLUG_RULES.assessment);
  const nameError =
    serverNameError ??
    fieldError(templateNameSchema, draft.name) ??
    slugProblemFor(derivedSlug, SLUG_RULES.assessment, { noun: "template" });
  const visibleNameError = showNameError || serverNameError ? nameError : null;

  const validate = (): string | null => {
    if (draft.gradeMin < 1 || draft.gradeMin > 12) return "Grade-min must be 1–12.";
    if (draft.gradeMax < 1 || draft.gradeMax > 12) return "Grade-max must be 1–12.";
    if (draft.gradeMin > draft.gradeMax) return "Grade-min must be ≤ grade-max.";
    if (!draft.initialTopicSlug) return "Pick at least one topic for the initial blueprint item.";
    if (draft.initialCount < 1 || draft.initialCount > 200)
      return "Initial item count must be 1–200.";
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage(null);
    setShowNameError(true);
    if (nameError) return;
    const validation = validate();
    if (validation) {
      setErrorMessage(validation);
      return;
    }
    const body: AssessmentTemplateCreate = {
      name: draft.name.trim(),
      slug: derivedSlug,
      description: draft.description.trim() || null,
      grade_band: { min: draft.gradeMin, max: draft.gradeMax },
      language: draft.language,
      items: [
        {
          topic_slugs: [draft.initialTopicSlug],
          count: draft.initialCount,
          type: draft.initialType,
          difficulty: draft.initialDifficulty === "__any__" ? null : draft.initialDifficulty,
          marks_per_question: 1.0,
          randomise_options: true,
        },
      ],
    };
    try {
      const created = await createMutation.mutateAsync(body);
      onCreated(created);
      onOpenChange(false);
    } catch (raw) {
      if (raw instanceof ApiError && raw.status === 409) {
        setServerNameError(DUPLICATE_NAME_MESSAGE);
        return;
      }
      const message =
        raw instanceof ApiError && raw.message
          ? raw.message
          : raw instanceof Error && raw.message
            ? raw.message
            : "Could not create template — please try again.";
      setErrorMessage(message);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full max-w-lg flex-col gap-4 overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>New assessment template</SheetTitle>
          <SheetDescription>
            Create the template in draft. You'll author its full blueprint, runtime config, and
            proctoring on the detail page before activating it.
          </SheetDescription>
        </SheetHeader>

        <form className="grid gap-4 pt-2" noValidate onSubmit={(e) => void handleSubmit(e)}>
          <div className="grid gap-1.5">
            <Label htmlFor="template-name">Name</Label>
            <Input
              id="template-name"
              value={draft.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={() => setShowNameError(true)}
              placeholder="Grade 6 — admissions entrance"
              maxLength={TEMPLATE_NAME_MAX}
              aria-invalid={visibleNameError ? true : undefined}
              aria-describedby={visibleNameError ? "template-name-error" : undefined}
              autoFocus
            />
            {visibleNameError ? (
              <p id="template-name-error" className="text-destructive text-xs">
                {visibleNameError}
              </p>
            ) : null}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="template-description">Description (optional)</Label>
            <Textarea
              id="template-description"
              value={draft.description}
              onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
              placeholder="What this paper is for and who takes it."
              maxLength={2000}
              rows={3}
            />
          </div>

          <fieldset className="grid gap-2 rounded-md border p-3">
            <legend className="text-foreground px-1 text-xs font-medium">Grade band</legend>
            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1">
                <Label htmlFor="template-grade-min" className="text-xs">
                  Min
                </Label>
                <Input
                  id="template-grade-min"
                  type="number"
                  min={1}
                  max={12}
                  value={draft.gradeMin}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, gradeMin: Number(e.target.value) || 1 }))
                  }
                />
              </div>
              <div className="grid gap-1">
                <Label htmlFor="template-grade-max" className="text-xs">
                  Max
                </Label>
                <Input
                  id="template-grade-max"
                  type="number"
                  min={1}
                  max={12}
                  value={draft.gradeMax}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, gradeMax: Number(e.target.value) || 1 }))
                  }
                />
              </div>
            </div>
            <span className="text-muted-foreground text-[11px]">
              Applicants whose grade falls outside this band can't be assigned this template.
            </span>
          </fieldset>

          <div className="grid gap-1.5">
            <Label>Default language</Label>
            <Select
              value={draft.language}
              onValueChange={(v) => setDraft((p) => ({ ...p, language: v as QuestionLanguage }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">{QUESTION_LANGUAGE_LABEL.en}</SelectItem>
                <SelectItem value="hi">{QUESTION_LANGUAGE_LABEL.hi}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <fieldset className="grid gap-2 rounded-md border p-3">
            <legend className="text-foreground px-1 text-xs font-medium">
              Initial blueprint item
            </legend>
            <span className="text-muted-foreground text-[11px]">
              A template must have ≥1 blueprint item. Add a starter row here; the full editor on the
              detail page lets you add more, tune difficulty mix, marks, and overrides.
            </span>

            <div className="grid gap-1.5">
              <Label className="text-xs">Topic</Label>
              <Select
                value={draft.initialTopicSlug || "__none__"}
                onValueChange={(v) =>
                  setDraft((p) => ({ ...p, initialTopicSlug: v === "__none__" ? "" : v }))
                }
                disabled={topicsQuery.isLoading || topics.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={topics.length === 0 ? "No topics yet" : "Pick a topic"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {topics.length === 0 ? (
                    <SelectItem value="__none__" disabled>
                      No topics yet — author one first
                    </SelectItem>
                  ) : (
                    topics.map((t) => (
                      <SelectItem key={t.slug} value={t.slug}>
                        {t.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="grid gap-1">
                <Label className="text-xs">Count</Label>
                <Input
                  type="number"
                  min={1}
                  max={200}
                  value={draft.initialCount}
                  onChange={(e) =>
                    setDraft((p) => ({ ...p, initialCount: Number(e.target.value) || 1 }))
                  }
                />
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Type</Label>
                <Select
                  value={draft.initialType}
                  onValueChange={(v) => setDraft((p) => ({ ...p, initialType: v as QuestionType }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPE_ORDER.map((t) => (
                      <SelectItem key={t} value={t}>
                        {QUESTION_TYPE_LABEL[t]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Difficulty</Label>
                <Select
                  value={draft.initialDifficulty}
                  onValueChange={(v) =>
                    setDraft((p) => ({
                      ...p,
                      initialDifficulty: v as DraftState["initialDifficulty"],
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any__">Any</SelectItem>
                    {QUESTION_DIFFICULTY_ORDER.map((d) => (
                      <SelectItem key={d} value={d}>
                        {QUESTION_DIFFICULTY_LABEL[d]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </fieldset>

          {errorMessage ? (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
            >
              <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <span className="flex-1">{errorMessage}</span>
            </div>
          ) : null}

          <div className="mt-2 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Creating…
                </>
              ) : (
                "Create draft"
              )}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
